import { memo } from "react";
import { Badge, Box, IconButton } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLiveEventsBellContext } from "context/LiveEventsContext";
import theme from "themeProvider";

const newEventPulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${alpha(theme.palette.primary.main, 0.6)}; }
  70% { box-shadow: 0 0 0 3px ${alpha(theme.palette.primary.main, 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(theme.palette.primary.main, 0)}; }
`;

const bellShakeFrames = (rest) => `
  0% { transform: rotate(0); }
  15% { transform: rotate(15deg); }
  30% { transform: rotate(-13deg); }
  45% { transform: rotate(10deg); }
  60% { transform: rotate(-7deg); }
  75% { transform: rotate(4deg); }
  100% { transform: ${rest}; }
`;
// Two same animations, switched per event to replay shake without reloading icon.
const bellShakeA = keyframes(bellShakeFrames("rotate(0)"));
const bellShakeB = keyframes(bellShakeFrames("rotate(0deg)"));

const countBadgeSx = {
  // Style only the count badge, not the inner connection dot.
  "& > .MuiBadge-badge": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    fontSize: 10,
    animation: `${newEventPulse} 1.5s ease-out infinite`,
  },
};

const getConnectionDotSx = (isConnected) => ({
  "& .MuiBadge-dot": {
    backgroundColor: isConnected
      ? theme.palette.success.dark
      : theme.palette.error.dark,
    boxShadow: `0 0 0 2px ${theme.palette.common.white}`,
  },
});

const expandIconSx = { ml: 0.5, color: theme.palette.text.secondary };

// Header bell: unread count, connection dot, and shake on new events.
const LiveEventsBell = () => {
  const { hasAccess, isConnected, unreadCount, isPanelOpen, togglePanel } =
    useLiveEventsBellContext();

  if (!hasAccess) return null;

  return (
    <IconButton
      onClick={togglePanel}
      disableRipple
      aria-label={
        unreadCount ? `Live events, ${unreadCount} unread` : "Live events"
      }
      aria-expanded={isPanelOpen}
      sx={{ mr: 3, "&:hover": { backgroundColor: "transparent" } }}
    >
      <Badge
        badgeContent={unreadCount}
        max={99}
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={countBadgeSx}
      >
        <Badge
          variant="dot"
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={getConnectionDotSx(isConnected)}
        >
          <Box
            sx={{
              display: "flex",
              width: 22,
              height: 22,
              transformOrigin: "top center",
              willChange: "transform",
              animation: unreadCount
                ? `${unreadCount % 2 ? bellShakeA : bellShakeB} 0.8s ease-in-out`
                : "none",
            }}
          >
            <object
              aria-label="Notifications"
              width={22}
              type="image/svg+xml"
              data="/assets/notification.svg"
              style={{ pointerEvents: "none" }}
            />
          </Box>
        </Badge>
      </Badge>
      {isPanelOpen ? (
        <ExpandLess sx={expandIconSx} />
      ) : (
        <ExpandMore sx={expandIconSx} />
      )}
    </IconButton>
  );
};

export default memo(LiveEventsBell);
